import React from 'react';
import { Box, Typography, Avatar, List, ListItem, ListItemAvatar, ListItemText, useTheme, styled } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: theme.spacing(3),
  height: '100%',
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: theme.palette.primary.main,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.12)',
    borderColor: theme.palette.error.main,
    '&::before': {
      opacity: 1,
      background: theme.palette.error.main,
    },
  },
}));

const StyledList = styled(Box)({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '16px',
});

const ClientColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  '& .MuiListItem-root': {
    padding: '8px 12px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&.top-client': {
      backgroundColor: 'rgba(46, 125, 50, 0.05)',
      '&:hover': {
        backgroundColor: 'rgba(46, 125, 50, 0.08)',
      },
    },
  },
});

const RankBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'rank',
})<{ rank: number }>(({ theme, rank }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 12,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: rank <= 3 ? theme.palette.common.white : theme.palette.text.secondary,
  backgroundColor: rank === 1 
    ? '#FFD700' 
    : rank === 2 
      ? '#C0C0C0' 
      : rank === 3 
        ? '#CD7F32' 
        : theme.palette.action.hover,
}));

interface Client {
  id: string;
  name: string;
  avatar?: string;
  value: number;
}

interface TopClientsCardProps {
  title: string;
  clients: Client[];
  valueSuffix?: string;
  maxItems?: number;
  color?: string;
  emptyMessage?: string;
}

const TopClientsCard: React.FC<TopClientsCardProps> = ({
  title,
  clients = [],
  valueSuffix = '',
  maxItems = 10,
  color,
  emptyMessage,
}) => {
  const theme = useTheme();
  
  if (!clients || clients.length === 0) {
    return (
      <StyledCard sx={{ '&::before': { background: color || theme.palette.primary.main } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2, 
            color: theme.palette.text.primary, 
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          {title}
        </Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: 200,
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          {emptyMessage || 'No hay datos disponibles'}
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard sx={{ '&::before': { background: color || theme.palette.primary.main } }}>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          color: theme.palette.text.primary, 
          fontWeight: 600,
          fontSize: '1rem',
        }}
      >
        {title}
      </Typography>
      
      <StyledList>
        {/* First 5 clients on the left */}
        <ClientColumn>
          {clients.slice(0, 5).map((client, index) => (
            <ListItem 
              key={client.id} 
              disableGutters 
              className="top-client"
              sx={{ 
                py: 1,
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              <RankBadge rank={index + 1}>
                {index < 3 ? <StarIcon sx={{ fontSize: '0.9rem' }} /> : index + 1}
              </RankBadge>
              <ListItemAvatar sx={{ minWidth: 32, mr: 1 }}>
                <Avatar 
                  src={client.avatar} 
                  alt={client.name}
                  sx={{ width: 28, height: 28, fontSize: '0.8rem' }}
                >
                  {client.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: index < 3 ? theme.palette.primary.dark : theme.palette.text.primary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {client.name}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    <span>{client.value} {valueSuffix}</span>
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </ListItem>
          ))}
        </ClientColumn>
        
        {/* Remaining clients on the right */}
        <ClientColumn>
          {clients.slice(5, maxItems).map((client, index) => (
            <ListItem 
              key={client.id} 
              disableGutters 
              sx={{ 
                py: 1,
                border: 'none',
              }}
            >
              <RankBadge rank={index + 6}>
                {index + 6}
              </RankBadge>
              <ListItemAvatar sx={{ minWidth: 32, mr: 1 }}>
                <Avatar 
                  src={client.avatar} 
                  alt={client.name}
                  sx={{ width: 28, height: 28, fontSize: '0.8rem' }}
                >
                  {client.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {client.name}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    <span>{client.value} {valueSuffix}</span>
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </ListItem>
          ))}
        </ClientColumn>
      </StyledList>
    </StyledCard>
  );
};

export default TopClientsCard;
